/**
 * useAuth — Emergent-managed Google SSO session lifecycle.
 *
 * Extracted from App.js during the code-quality refactor. Owns:
 *   - the OAuth callback exchange (#session_id → httpOnly cookie),
 *   - the silent /auth/me check on boot,
 *   - the hero-page gate (guests dismiss it; signed-in users bypass it),
 *   - sign-in redirect and sign-out.
 *
 * Auth tokens live in httpOnly cookies only — localStorage carries just
 * UI preferences (hero dismissed / context applied), never credentials.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

import { BACKEND_URL, logDev, safeStorage } from "../lib/beaconUtils";

const AUTH_API = `${BACKEND_URL}/api/auth`;

export function useAuth({ onSignedOut } = {}) {
  // `authUser` is null while unknown/anonymous and a user object once
  // /api/auth/me returns. `authChecking` gates the UI so we don't flash
  // the Hero for signed-in users.
  const [authUser, setAuthUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  // Surfaced on the hero page when the OAuth exchange fails — a silent
  // bounce back to the hero is undiagnosable for users in production.
  const [authError, setAuthError] = useState(null);

  // Detect the OAuth callback SYNCHRONOUSLY during render so we don't
  // flash the hero for a returning user. useEffect fires too late.
  const hasSessionIdInHash =
    typeof window !== "undefined" &&
    Boolean(window.location.hash?.includes("session_id="));

  // Hero Page gate — dismissed once the user has either signed in or
  // chosen guest mode. Guest choice is remembered in localStorage.
  const [heroDismissed, setHeroDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("beacon-hero-dismissed") === "true";
  });

  // Auth boot:
  // 1. If #session_id=... is in the URL, exchange it for a cookie session
  //    (OAuth callback), then strip the fragment.
  // 2. Otherwise hit /api/auth/me with credentials to detect an existing
  //    cookie session. Guests get authUser=null.
  const exchangeSessionFromHash = useCallback(async () => {
    const match = window.location.hash?.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    // Strip the fragment first so refreshes / duplicate invocations
    // never retry a spent session_id.
    if (window.history && window.history.replaceState) {
      window.history.replaceState(
        null, "", window.location.pathname + window.location.search,
      );
    }
    if (!sessionId) return false;
    try {
      const r = await axios.post(
        `${AUTH_API}/session`,
        { session_id: sessionId },
        { withCredentials: true },
      );
      setAuthUser(r.data.user);
      setAuthError(null);
      // Signed-in users always bypass the Hero.
      setHeroDismissed(true);
      safeStorage.set("beacon-hero-dismissed", "true");
      return true;
    } catch (err) {
      /* Session exchange failed — tell the user, don't crash. */
      logDev("Auth session exchange failed", err);
      const status = err.response?.status;
      const detail =
        err.response?.data?.detail || err.message || "Unknown error";
      setAuthError(status ? `${detail} (HTTP ${status})` : detail);
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const bootAuth = async () => {
      try {
        if (hasSessionIdInHash) {
          await exchangeSessionFromHash();
        } else {
          const r = await axios.get(`${AUTH_API}/me`, {
            withCredentials: true,
            validateStatus: (s) => s < 500,
          });
          if (!cancelled && r.status === 200) {
            setAuthUser(r.data);
            setHeroDismissed(true);
          }
        }
      } finally {
        if (!cancelled) setAuthChecking(false);
      }
    };
    bootAuth();
    return () => { cancelled = true; };
  }, [hasSessionIdInHash, exchangeSessionFromHash]);

  // PRODUCTION BUG GUARD: when the OAuth provider auto-redirects quickly
  // (user already authenticated at auth.emergentagent.com), the browser
  // can restore this page from the back/forward cache instead of doing a
  // fresh load. React never remounts, the boot effect has already run,
  // and the freshly arrived #session_id would be silently ignored — the
  // "sign-in bounces back to the hero" loop. Catch late fragment
  // arrivals via pageshow (bfcache restore) and hashchange.
  useEffect(() => {
    const maybeExchange = () => {
      if (window.location.hash?.includes("session_id=")) {
        setAuthChecking(true);
        exchangeSessionFromHash().finally(() => setAuthChecking(false));
      }
    };
    const onPageShow = (e) => { if (e.persisted) maybeExchange(); };
    window.addEventListener("hashchange", maybeExchange);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("hashchange", maybeExchange);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [exchangeSessionFromHash]);

  const dismissHero = () => {
    setHeroDismissed(true);
    safeStorage.set("beacon-hero-dismissed", "true");
  };

  const signInWithGoogle = () => {
    setAuthError(null);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
    // URLS, THIS BREAKS THE AUTH. Emergent's auth relies on the exact
    // runtime origin so the session_id is redeemed on the same host it
    // was minted for.
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const signOut = async () => {
    try {
      await axios.post(`${AUTH_API}/logout`, null, { withCredentials: true });
    } catch (err) {
      // Cookie clears client-side regardless — a network failure here is
      // recoverable, just log for debugging.
      logDev("Logout request failed (session still cleared locally).", err);
    }
    setAuthUser(null);
    setHeroDismissed(false);
    safeStorage.remove("beacon-hero-dismissed");
    safeStorage.remove("beacon-context-applied");
    // Let the app wipe in-memory profile state so nothing bleeds
    // across sessions.
    onSignedOut?.();
  };

  return {
    authUser, authChecking, heroDismissed, authError,
    dismissHero, signInWithGoogle, signOut,
  };
}
